import { escapeHtml, isElement } from '@src/utils/html';
import { getTextNodesFromDOM } from '@src/utils/wysiwyg_editor/dom';
import { isIE } from 'react-device-detect';

function getParent(
  node: Text | HTMLElement | null | void
): Node | HTMLElement | null {
  if (isIE) {
    let parent = node?.parentElement || node?.parentNode;

    while (parent && !isElement(parent)) {
      parent = node?.parentElement || node?.parentNode || null;
    }

    return parent && isElement(parent) ? parent : null;
  }

  return node?.parentElement || null;
}

function getTextRanges(textNodes) {
  const ranges = [];
  let consumedOffset = 0;
  let combinedTextContent = textNodes.reduce((allText, node) => {
    return allText + node.textContent;
  }, '');

  textNodes.forEach(function (node) {
    const parent = getParent(node);
    let text = node.textContent;
    const words = [];
    let startPtr = 0;
    let endPtr = 1;

    if (text.length === 0) {
      words.push('');
    }

    while (endPtr <= text.length) {
      const lastChar = text[endPtr - 1];
      const nextChar = text[endPtr];
      let rangeText = text.slice(startPtr, endPtr);

      // We use `white-space: pre-wrap;`, which breaks on spaces and hyphens
      if (lastChar === '-') {
        // flow through
      } else if (/^ +$/.test(rangeText)) {
        if (nextChar === ' ') {
          endPtr = endPtr + 1;
          continue;
        }
      } else if (lastChar === ' ') {
        endPtr = endPtr - 1;
        rangeText = text.slice(startPtr, endPtr);
      } else if (endPtr < text.length) {
        endPtr = endPtr + 1;
        continue;
      }

      words.push(rangeText);
      startPtr = endPtr;
      endPtr = startPtr + 1;
    }

    parent.innerHTML = words
      .map((word) => {
        if (word === '') {
          return '<empty-paragraph>&ZeroWidthSpace;</empty-paragraph>';
        } else {
          return `<span>${escapeHtml(word)}</span>`;
        }
      })
      .join('');

    Array.from(parent.querySelectorAll('span')).forEach((span) => {
      const textContent = span.textContent;
      const nextChar = combinedTextContent[consumedOffset + textContent.length];
      const nextWordIsWhitespace = nextChar === ' ';

      const range = {
        ...wordMetrics(span, nextWordIsWhitespace),
        t: textContent
      };

      ranges.push(range);
      consumedOffset += span.textContent.length;
    });
  });

  return ranges;
}

function wordMetrics(node: Node, nextWordIsSingleSpace: boolean) {
  if (!node) return { a: 0, d: 0, h: 0, w: 0, x: 0, y: 0 };

  const offsetParent = node.offsetParent;
  const elRect = node.getBoundingClientRect();
  const parentRect = offsetParent.getBoundingClientRect();

  const elLeft = elRect.left - parentRect.left;
  const elTop = elRect.top - parentRect.top;
  const height = elRect.height;
  const width = elRect.width;

  const ascentDetectionNode = document.createElement('span');
  ascentDetectionNode.setAttribute(
    'style',
    'width: 0px; height: 0px; display: inline-block;'
  );

  node.appendChild(ascentDetectionNode);

  const ascentRect = ascentDetectionNode.getBoundingClientRect();
  const elRectWithAscent = node.getBoundingClientRect();
  let ascent = ascentRect.top - elRectWithAscent.top;

  // There is a bug that happens in the following scenario:
  // - Browser is IE
  // - A word fits at the end of a line, but gets wrapped to the next line
  //   because it has a space after it (this only happens in IE)
  // - It has a space after it, not any other character (part of the requirement
  //   above)
  //
  // Explanation of the bug:
  // We insert an HTML element between the text content and the next HTML
  // element, which we then ask for its measurements. In IE, this breaks the
  // wrapping flow, and the word gets moved up to the line where it actually
  // fits (which is where is already is in every other browser). Then the
  // measurements are all wrong because the word is not where we expect it to
  // be. Because of that, the word is located in the wrong place when we use our
  // measurements.
  //
  // Explanation of the fix:
  // We take the difference of the element's original location Y and the
  // location Y after we add the measurement node, and subtract it from the
  // measured ascent value. This adjusts the ascent measurement to be correct
  // because without this measurement, the ascent ends up being the height of
  // the full line plus the ascent value. The adjustment has the affect of
  // removing the height of the extra line from the calculated ascent value.
  if (isIE && nextWordIsSingleSpace) {
    const flowChangeY = elRect.top - elRectWithAscent.top;
    ascent = ascent - flowChangeY;
  }

  node.removeChild(ascentDetectionNode);

  return {
    a: ascent,
    d: height - ascent,
    h: height,
    w: width,
    x: elLeft,
    y: elTop + ascent
  };
}

export function getMeasurements(node: HTMLElement) {
  let allRanges = [];

  Array.from(node.querySelectorAll('p')).forEach((paragraph) => {
    const textNodes = getTextNodesFromDOM(paragraph);
    const ranges = getTextRanges(textNodes);
    allRanges = allRanges.concat(ranges);
  });

  let accumulatedOffset = 0;

  allRanges = allRanges
    .map(function (range) {
      const rangeWithOffset = {
        ...range,
        o: accumulatedOffset,
        l: range.t.length
      };

      accumulatedOffset += rangeWithOffset.l;

      return rangeWithOffset;
    })
    .filter((range) => /\S/.test(range.t))
    .map((range) => {
      const { t, ...rest } = range;
      return rest;
    });

  return allRanges;
}
